import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  doc,
  setDoc,
  getDoc,
  getFirestore,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, getStorage } from "firebase/storage";
import {
  Camera,
  ChevronLeft,
  Check,
  X,
  Loader2,
  Calendar,
  User,
  Phone,
  AtSign,
  Mail,
  Save,
} from "lucide-react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import { useUserAuth } from "../../context/userAuthContext";

const UserForm = () => {
  const navigate = useNavigate();
  const { user } = useUserAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [checkingNickname, setCheckingNickname] = useState(false);
  const [formData, setFormData] = useState({
    nombre: "",
    apellido: "",
    celular: "",
    fechaNacimiento: "",
    sexo: "",
    nickname: "",
    profileImage: null,
    profileImageUrl: "",
  });
  const [errors, setErrors] = useState({
    nickname: "",
    fechaNacimiento: "",
  });
  const [nicknameAvailable, setNicknameAvailable] = useState(null);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) return;
      try {
        const firestore = getFirestore();
        const docRef = doc(firestore, "Users", user.uid);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const userData = docSnap.data();
          let fechaNacimiento = userData.fechaNacimiento || "";
          if (!fechaNacimiento && userData.edad) {
            const today = new Date();
            const birthYear = today.getFullYear() - parseInt(userData.edad);
            fechaNacimiento = `${birthYear}-01-01`;
          }
          setFormData({
            nombre: userData.nombre || "",
            apellido: userData.apellido || "",
            celular: userData.celular || "",
            fechaNacimiento,
            sexo: userData.sexo || "",
            nickname: userData.nickname || "",
            profileImage: null,
            profileImageUrl: userData.photoUrl || "",
          });
          if (userData.nickname) setNicknameAvailable(true);
        }
      } catch (error) {
        console.error("Error al cargar datos:", error);
        setMessage({
          text: "Error al cargar los datos del usuario",
          type: "error",
        });
      } finally {
        setLoading(false);
      }
    };
    fetchUserData();
  }, [user]);

  const validateNickname = async (nickname) => {
    if (!nickname) {
      setNicknameAvailable(null);
      setErrors((prev) => ({ ...prev, nickname: "" }));
      return false;
    }
    setCheckingNickname(true);
    const nicknameRegex = /^[a-z0-9]+$/;
    if (!nicknameRegex.test(nickname)) {
      setErrors((prev) => ({
        ...prev,
        nickname:
          "Solo minúsculas y números, sin espacios ni caracteres especiales",
      }));
      setNicknameAvailable(false);
      setCheckingNickname(false);
      return false;
    }
    try {
      const firestore = getFirestore();
      const usersRef = collection(firestore, "Users");
      const q = query(usersRef, where("nickname", "==", nickname));
      const querySnapshot = await getDocs(q);
      const isOwnNickname = querySnapshot.docs.some(
        (doc) => doc.id === user.uid
      );
      if (querySnapshot.empty || isOwnNickname) {
        setErrors((prev) => ({ ...prev, nickname: "" }));
        setNicknameAvailable(true);
        setCheckingNickname(false);
        return true;
      } else {
        setErrors((prev) => ({
          ...prev,
          nickname: "Este nickname ya está en uso",
        }));
        setNicknameAvailable(false);
        setCheckingNickname(false);
        return false;
      }
    } catch (error) {
      console.error("Error validando nickname:", error);
      setMessage({ text: "Error al validar el nickname", type: "error" });
      setCheckingNickname(false);
      return false;
    }
  };

  const validateFechaNacimiento = (date) => {
    if (!date) {
      setErrors((prev) => ({ ...prev, fechaNacimiento: "Fecha requerida" }));
      return false;
    }
    const birthDate = new Date(date);
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();
    const hasBirthdayOccurred =
      today.getMonth() > birthDate.getMonth() ||
      (today.getMonth() === birthDate.getMonth() &&
        today.getDate() >= birthDate.getDate());
    const calculatedAge = hasBirthdayOccurred ? age : age - 1;
    if (calculatedAge < 18) {
      setErrors((prev) => ({
        ...prev,
        fechaNacimiento: "Debes ser mayor de 18 años",
      }));
      return false;
    }
    if (calculatedAge > 120) {
      setErrors((prev) => ({ ...prev, fechaNacimiento: "Fecha no válida" }));
      return false;
    }
    setErrors((prev) => ({ ...prev, fechaNacimiento: "" }));
    return true;
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        profileImage: file,
        profileImageUrl: URL.createObjectURL(file),
      }));
    }
  };

  const uploadImage = async (file) => {
    if (!file) return null;
    try {
      const storage = getStorage();
      const storageRef = ref(storage, `users/${user.uid}/profile.jpg`);
      await uploadBytes(storageRef, file);
      return await getDownloadURL(storageRef);
    } catch (error) {
      console.error("Error al subir imagen:", error);
      throw new Error("Error al subir la imagen de perfil");
    }
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "nickname") await validateNickname(value);
    if (name === "fechaNacimiento") validateFechaNacimiento(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      setMessage({ text: "No hay usuario autenticado", type: "error" });
      return;
    }
    setLoading(true);
    try {
      const isNicknameValid = await validateNickname(formData.nickname);
      const isFechaNacimientoValid = validateFechaNacimiento(
        formData.fechaNacimiento
      );
      if (
        !isNicknameValid ||
        !isFechaNacimientoValid ||
        !formData.nombre ||
        !formData.apellido
      ) {
        setMessage({
          text: "Por favor, completa todos los campos requeridos correctamente",
          type: "error",
        });
        setLoading(false);
        return;
      }
      const birthDate = new Date(formData.fechaNacimiento);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < birthDate.getDate())
      ) {
        age--;
      }
      let photoUrl = formData.profileImageUrl;
      if (formData.profileImage) {
        photoUrl = await uploadImage(formData.profileImage);
      }
      const firestore = getFirestore();
      await setDoc(doc(firestore, "Users", user.uid), {
        nombre: formData.nombre,
        apellido: formData.apellido,
        celular: formData.celular,
        fechaNacimiento: formData.fechaNacimiento,
        edad: age.toString(),
        sexo: formData.sexo,
        nickname: formData.nickname,
        photoUrl: photoUrl || "",
        uid: user.uid,
        email: user.email,
      });
      setMessage({
        text: "¡Perfil creado/actualizado correctamente!",
        type: "success",
      });
      setTimeout(() => navigate("/settings"), 1500);
    } catch (error) {
      console.error("Error al guardar:", error);
      setMessage({
        text: error.message || "Error al guardar los datos",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#FFBC00] flex items-center justify-center px-4">
        <div className="bg-[#F5EBDD] border-2 border-[#FF0000] p-4 sm:p-6 rounded-none shadow-xl max-w-md w-full text-center">
          <X className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 text-[#FF0000]" />
          <p className="text-[#000000] text-sm sm:text-base font-bold uppercase tracking-wide">
            Por favor, inicia sesión para acceder a esta página
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFBC00]">
      <div className="max-w-md mx-auto px-4 sm:px-6 md:px-8 pt-4 sm:pt-6">
        {/* Header */}
        <div className="sticky top-0 bg-[#F5EBDD] z-10 py-3 sm:py-4 mb-4 sm:mb-6 flex items-center justify-between border-b-2 border-[#FF0000] shadow-sm rounded-none">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-[#FFF8E1] rounded-none transition-all active:scale-95"
            aria-label="Volver"
          >
            <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 text-[#FF0000]" />
          </button>
          <h1 className="text-base sm:text-lg font-bold text-[#FF0000] uppercase tracking-wide">
            Crear/Editar Perfil
          </h1>
          <button
            type="button"
            onClick={handleSubmit}
            className="p-2 sm:p-3 bg-[#FF0000] text-[#F5EBDD] rounded-none hover:bg-[#000000] hover:text-[#FFBC00] transition-all active:scale-95"
            aria-label="Guardar"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin" />
            ) : (
              <Save className="w-5 h-5 sm:w-6 sm:h-6" />
            )}
          </button>
        </div>

        {/* Profile Image */}
        <div className="bg-[#F5EBDD] border-2 border-[#FF0000] rounded-none shadow-xl mb-6 p-4 sm:p-6">
          <div className="flex justify-center mb-4">
            <div className="relative">
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-none overflow-hidden bg-[#FFF8E1] border-2 border-[#FF0000] shadow-md">
                {formData.profileImageUrl ? (
                  <img
                    src={formData.profileImageUrl}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-[#FFF8E1]">
                    <User className="w-10 h-10 sm:w-12 sm:h-12 text-[#FF0000]" />
                  </div>
                )}
              </div>
              <label className="absolute bottom-0 right-0 bg-[#FF0000] p-2 rounded-none cursor-pointer hover:bg-[#000000] transition-all">
                <Camera className="w-4 h-4 sm:w-5 sm:h-5 text-[#F5EBDD]" />
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </label>
            </div>
          </div>
          <div className="text-center">
            <h2 className="text-base sm:text-lg font-bold text-[#FF0000] uppercase tracking-wide">
              {formData.nombre && formData.apellido
                ? `${formData.nombre} ${formData.apellido}`
                : "Completa tu perfil"}
            </h2>
            {formData.nickname && (
              <p className="text-[#000000] text-sm sm:text-base font-bold">
                @{formData.nickname}
              </p>
            )}
          </div>
        </div>

        {/* Form content */}
        <div className="bg-[#F5EBDD] border-2 border-[#FF0000] rounded-none shadow-xl mb-6 p-4 sm:p-6">
          <div className="border-b-2 border-[#FF0000] px-4 py-3 sm:px-6 sm:py-4">
            <h2 className="text-base sm:text-lg font-bold text-[#FF0000] uppercase tracking-wide">
              Información de Perfil
            </h2>
          </div>
          <form
            onSubmit={handleSubmit}
            className="p-4 sm:p-6 space-y-4 sm:space-y-6"
          >
            {/* Nombre Completo */}
            <div className="space-y-2">
              <div className="flex items-center text-xs sm:text-sm text-[#FF0000] font-bold uppercase tracking-wide">
                <User className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                <label>Nombre Completo</label>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <input
                  type="text"
                  name="nombre"
                  placeholder="Nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  required
                  className="p-3 border-2 border-[#FF0000] rounded-none bg-[#F5EBDD] text-[#000000] focus:border-[#FFBC00] focus:outline-none hover:bg-[#FFF8E1] transition-all w-full placeholder-[#FF0000] font-medium text-sm sm:text-base"
                />
                <input
                  type="text"
                  name="apellido"
                  placeholder="Apellido"
                  value={formData.apellido}
                  onChange={handleChange}
                  required
                  className="p-3 border-2 border-[#FF0000] rounded-none bg-[#F5EBDD] text-[#000000] focus:border-[#FFBC00] focus:outline-none hover:bg-[#FFF8E1] transition-all w-full placeholder-[#FF0000] font-medium text-sm sm:text-base"
                />
              </div>
            </div>

            {/* Nickname */}
            <div className="space-y-2">
              <div className="flex items-center text-xs sm:text-sm text-[#FF0000] font-bold uppercase tracking-wide">
                <AtSign className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                <label>Nickname</label>
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-[#FF0000] font-bold">@</span>
                </div>
                <input
                  type="text"
                  name="nickname"
                  value={formData.nickname}
                  onChange={handleChange}
                  required
                  placeholder="nickname"
                  className={`w-full p-3 pl-8 border-2 border-[#FF0000] rounded-none bg-[#F5EBDD] text-[#000000] focus:border-[#FFBC00] focus:outline-none hover:bg-[#FFF8E1] transition-all font-medium text-sm sm:text-base placeholder-[#FF0000] ${
                    errors.nickname
                      ? "border-[#FF0000] focus:border-[#FF0000]"
                      : ""
                  }`}
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  {checkingNickname ? (
                    <Loader2 className="w-5 h-5 text-[#FF0000] animate-spin" />
                  ) : nicknameAvailable ? (
                    <Check className="w-5 h-5 text-[#FFBC00]" />
                  ) : errors.nickname ? (
                    <X className="w-5 h-5 text-[#FF0000]" />
                  ) : null}
                </div>
              </div>
              {errors.nickname && (
                <p className="text-xs sm:text-sm text-[#FF0000] font-medium">
                  {errors.nickname}
                </p>
              )}
            </div>

            {/* Email */}
            <div className="space-y-2">
              <div className="flex items-center text-xs sm:text-sm text-[#FF0000] font-bold uppercase tracking-wide">
                <Mail className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                <label>Email</label>
              </div>
              <input
                type="email"
                value={user.email}
                disabled
                className="w-full p-3 border-2 border-[#FF0000] rounded-none bg-[#FFF8E1] text-[#000000]/50 font-medium text-sm sm:text-base cursor-not-allowed"
              />
              <p className="text-xs text-[#000000] font-medium">
                El email no se puede modificar
              </p>
            </div>

            {/* Fecha de Nacimiento */}
            <div className="space-y-2">
              <div className="flex items-center text-xs sm:text-sm text-[#FF0000] font-bold uppercase tracking-wide">
                <Calendar className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                <label>Fecha de Nacimiento</label>
              </div>
              <input
                type="date"
                name="fechaNacimiento"
                value={formData.fechaNacimiento}
                onChange={handleChange}
                required
                className="p-3 border-2 border-[#FF0000] rounded-none bg-[#F5EBDD] text-[#000000] focus:border-[#FFBC00] focus:outline-none hover:bg-[#FFF8E1] transition-all w-full font-medium text-sm sm:text-base"
              />
              {errors.fechaNacimiento && (
                <p className="text-xs sm:text-sm text-[#FF0000] font-medium">
                  {errors.fechaNacimiento}
                </p>
              )}
            </div>

            {/* Teléfono */}
            <div className="space-y-2">
              <div className="flex items-center text-xs sm:text-sm text-[#FF0000] font-bold uppercase tracking-wide">
                <Phone className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                <label>Teléfono</label>
              </div>
              <PhoneInput
                country={"mx"}
                value={formData.celular}
                onChange={(phone) =>
                  setFormData((prev) => ({ ...prev, celular: phone }))
                }
                containerClass="w-full"
                inputClass="w-full p-3 border-2 border-[#FF0000] rounded-none bg-[#F5EBDD] text-[#000000] focus:border-[#FFBC00] focus:outline-none hover:bg-[#FFF8E1] transition-all font-medium text-sm sm:text-base"
                buttonClass="bg-[#F5EBDD] border-2 border-[#FF0000] rounded-none hover:bg-[#FFF8E1]"
                dropdownClass="bg-[#F5EBDD] border-2 border-[#FF0000] rounded-none shadow-lg"
              />
            </div>

            {/* Sexo */}
            <div className="space-y-2">
              <div className="flex items-center text-xs sm:text-sm text-[#FF0000] font-bold uppercase tracking-wide">
                <User className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                <label>Sexo</label>
              </div>
              <select
                name="sexo"
                value={formData.sexo}
                onChange={handleChange}
                required
                className="p-3 border-2 border-[#FF0000] rounded-none bg-[#F5EBDD] text-[#000000] focus:border-[#FFBC00] focus:outline-none hover:bg-[#FFF8E1] transition-all w-full font-medium text-sm sm:text-base"
              >
                <option value="">Seleccionar</option>
                <option value="masculino">Masculino</option>
                <option value="femenino">Femenino</option>
                <option value="otro">Otro</option>
              </select>
            </div>

            <div className="mt-6 sm:mt-8">
              <button
                type="submit"
                className="w-full bg-[#FF0000] text-[#F5EBDD] font-bold uppercase tracking-wide py-3 sm:py-4 rounded-none hover:bg-[#000000] hover:text-[#FFBC00] transition-all active:scale-95 flex items-center justify-center"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
                ) : (
                  <Save className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
                )}
                Guardar Cambios
              </button>
            </div>
          </form>
        </div>

        {/* Toast Message */}
        {message.text && (
          <div
            className={`fixed bottom-4 left-4 right-4 max-w-xs sm:max-w-sm mx-auto p-3 rounded-none shadow-xl border-2 border-[#FF0000] ${
              message.type === "error"
                ? "bg-[#FF0000] text-[#F5EBDD]"
                : "bg-[#FFBC00] text-[#000000]"
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                {message.type === "error" ? (
                  <X className="w-5 h-5 mr-2 text-[#F5EBDD]" />
                ) : (
                  <Check className="w-5 h-5 mr-2 text-[#000000]" />
                )}
                <span className="text-xs sm:text-sm font-bold uppercase tracking-wide">
                  {message.text}
                </span>
              </div>
              <button
                onClick={() => setMessage({ text: "", type: "" })}
                className="p-1 hover:bg-[#FFF8E1]/50 rounded-none transition-all"
                aria-label="Cerrar"
              >
                <X className="w-4 h-4 text-[#000000]" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserForm;
